import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";

import AssistedRegistrationExpertApi, {
  CORRECTABLE_FIELD_OPTIONS,
} from "../../../../../infrastructure/api/assistedRegistrationExpertApi";
import MarketGrowthPageShell from "../components/MarketGrowthPageShell";

const AssistedRegistrationExpertReviewPage = () => {
  const [loading, setLoading] = useState(false);
  const [queue, setQueue] = useState([]);
  const [correctionLead, setCorrectionLead] = useState(null);
  const [correctionNote, setCorrectionNote] = useState("");
  const [selectedFields, setSelectedFields] = useState([]);

  const loadQueue = useCallback(async () => {
    setLoading(true);
    try {
      const res = await AssistedRegistrationExpertApi.getQueue();
      if (!res.isSuccess) {
        toast.error(res.message);
        return;
      }
      setQueue(res.data || []);
    } catch {
      toast.error("بارگذاری صف بررسی ناموفق بود.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadQueue();
  }, [loadQueue]);

  const handleApprove = async (leadId) => {
    setLoading(true);
    try {
      const res = await AssistedRegistrationExpertApi.approve(leadId);
      if (!res.isSuccess) {
        toast.error(res.message);
        return;
      }
      toast.success("بررسی اولیه تأیید شد.");
      await loadQueue();
    } catch {
      toast.error("تأیید ناموفق بود.");
    } finally {
      setLoading(false);
    }
  };

  const openCorrectionDialog = (item) => {
    setCorrectionLead(item);
    setCorrectionNote("");
    setSelectedFields([]);
  };

  const closeCorrectionDialog = () => {
    setCorrectionLead(null);
    setCorrectionNote("");
    setSelectedFields([]);
  };

  const toggleField = (key) => {
    setSelectedFields((prev) =>
      prev.includes(key) ? prev.filter((f) => f !== key) : [...prev, key]
    );
  };

  const handleRequestCorrection = async () => {
    if (!correctionLead) return;
    if (!correctionNote.trim()) {
      toast.error("توضیحات اصلاح الزامی است.");
      return;
    }
    if (!selectedFields.length) {
      toast.error("حداقل یک فیلد قابل اصلاح انتخاب کنید.");
      return;
    }
    setLoading(true);
    try {
      const res = await AssistedRegistrationExpertApi.requestCorrection({
        leadId: correctionLead.leadId,
        correctionNote: correctionNote.trim(),
        correctableFields: selectedFields,
      });
      if (!res.isSuccess) {
        toast.error(res.message);
        return;
      }
      toast.success("درخواست اصلاح برای متقاضی ارسال شد.");
      closeCorrectionDialog();
      await loadQueue();
    } catch {
      toast.error("ثبت درخواست اصلاح ناموفق بود.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <MarketGrowthPageShell
      title="بررسی اولیه ثبت نماینده"
      description="تأیید یا درخواست اصلاح پرونده‌های ثبت‌شده توسط نمایندگان."
      loading={loading}
    >
      <Alert severity="info" sx={{ mb: 2 }}>
        پس از تأیید، پرونده برای همه نقش‌ها غیرقابل ویرایش می‌شود. در حالت نیاز به اصلاح،
        فقط متقاضی می‌تواند فیلدهای مشخص‌شده را ویرایش کند.
      </Alert>

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>کد لید</TableCell>
            <TableCell>موبایل</TableCell>
            <TableCell>وضعیت</TableCell>
            <TableCell>تاریخ ارسال</TableCell>
            <TableCell align="left">عملیات</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {queue.length === 0 ? (
            <TableRow>
              <TableCell colSpan={5}>
                <Typography variant="body2" color="text.secondary">
                  پرونده‌ای در صف بررسی نیست.
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            queue.map((item) => (
              <TableRow key={item.leadId}>
                <TableCell>{item.leadCode}</TableCell>
                <TableCell>{item.maskedMobile}</TableCell>
                <TableCell>{item.statusTitle}</TableCell>
                <TableCell>
                  {item.submittedAt
                    ? new Date(item.submittedAt).toLocaleDateString("fa-IR")
                    : "—"}
                </TableCell>
                <TableCell align="left">
                  <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
                    <Button
                      size="small"
                      variant="contained"
                      color="success"
                      onClick={() => handleApprove(item.leadId)}
                    >
                      تأیید
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      color="warning"
                      onClick={() => openCorrectionDialog(item)}
                    >
                      نیاز به اصلاح
                    </Button>
                  </Box>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      <Dialog open={Boolean(correctionLead)} onClose={closeCorrectionDialog} maxWidth="sm" fullWidth>
        <DialogTitle>درخواست اصلاح — {correctionLead?.leadCode}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            minRows={3}
            label="توضیحات اصلاح برای متقاضی"
            value={correctionNote}
            onChange={(e) => setCorrectionNote(e.target.value)}
            sx={{ mt: 1, mb: 2 }}
          />
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            فیلدهای قابل اصلاح
          </Typography>
          <FormGroup>
            {CORRECTABLE_FIELD_OPTIONS.map((opt) => (
              <FormControlLabel
                key={opt.key}
                control={
                  <Checkbox
                    checked={selectedFields.includes(opt.key)}
                    onChange={() => toggleField(opt.key)}
                  />
                }
                label={opt.label}
              />
            ))}
          </FormGroup>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCorrectionDialog}>انصراف</Button>
          <Button variant="contained" onClick={handleRequestCorrection}>
            ارسال به متقاضی
          </Button>
        </DialogActions>
      </Dialog>
    </MarketGrowthPageShell>
  );
};

export default AssistedRegistrationExpertReviewPage;
